import axios from 'axios';
import dayjs from 'dayjs';
import { API_get_forecast_accuracy } from '../../../../../../constants';
import { getDefaultAxiosConfig } from '../../../../../../utilities/requests';

export function summarizeAccuracy(rows) {
  const scored = rows.filter((r) => !r.exclusion);
  const result = { expected: rows.length, scored: scored.length, improved: 0, worsened: 0, unchanged: 0, unadjusted: 0, exclusions: {} };
  rows.forEach((r) => { if (r.exclusion) result.exclusions[r.exclusion] = (result.exclusions[r.exclusion] || 0) + 1; else result[r.outcome] += 1; });
  ['baseline', 'adjusted'].forEach((kind) => {
    result[`${kind}_mape`] = scored.length ? scored.reduce((n, r) => n + r[`${kind}_ape`], 0) / scored.length : null;
    result[`${kind}_wape`] = scored.length ? 100 * scored.reduce((n, r) => n + Math.abs(r[kind] - r.actual), 0) / scored.reduce((n, r) => n + r.actual, 0) : null;
  });
  return result;
}

export function accuracySeries(rows, grain) {
  const groups = new Map();
  rows.forEach((row) => {
    const d = dayjs(row.date);
    const key = grain === 'week' ? d.subtract((d.day() + 6) % 7, 'day').format('YYYY-MM-DD') : row.date;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups].sort(([a], [b]) => a.localeCompare(b)).map(([date, values]) => ({ date, ...summarizeAccuracy(values) }));
}

export async function loadAccuracyReport(stores, start, end, { isCancelled, onProgress }) {
  const rows = [], summary = [];
  for (const [index, store] of stores.entries()) {
    if (isCancelled()) throw new Error('Cancelled');
    onProgress(`Checking ${store.store_name} (${index + 1} of ${stores.length})…`);
    const response = await axios.get(API_get_forecast_accuracy, { ...getDefaultAxiosConfig(), timeout: 60000, params: { store_id: store.id, company_id: store.company, date_start: start, date_end: end } });
    const data = response.data;
    rows.push(...data.rows.map((r) => ({ ...r, location: store.store_name })));
    summary.push({ location: store.store_name, ...data.summary });
  }
  return { rows, summary, total: summarizeAccuracy(rows) };
}


// Keep every exported field even when the first day has missing data.
export function accuracyDetailExportRows(rows) {
  return rows.map((row) => ({
    location: row.location ?? null,
    date: row.date ?? null,
    baseline: row.baseline == null ? null : Number(row.baseline.toFixed(2)),
    adjusted: row.adjusted == null ? null : Number(row.adjusted.toFixed(2)),
    actual: row.actual == null ? null : Number(row.actual.toFixed(2)),
    baseline_ape: row.baseline_ape ?? null,
    adjusted_ape: row.adjusted_ape ?? null,
    outcome: row.outcome ?? null,
  }));
}

// Only actionable conflicts/invalid values, or a missing forecast where sales
// actually exist, warrant a short notice. Empty/closed days are not alerts.
export function accuracyInputIssues(rows) {
  const labels = { duplicate_actual: "conflicting sales totals", invalid_actual: "invalid sales values",
    duplicate_forecast: "conflicting forecasts", invalid_forecast: "invalid forecast values",
    duplicate_adjustment: "conflicting adjustments", invalid_adjustment: "invalid adjustment values" };
  const groups = new Map();
  rows.forEach((r) => {
    const issue = labels[r.exclusion] || (r.exclusion === 'missing_forecast' && r.actual > 0 ? 'sales without a forecast' : null);
    if (!issue) return;
    const key = `${r.location}: ${issue}`;
    groups.set(key, (groups.get(key) || 0) + 1);
  });
  return [...groups].map(([label, count]) => `${label} on ${count} ${count === 1 ? 'day' : 'days'}.`);
}
